import React from "react";
import {
  Card,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import useDeviceType from "../../hooks/useDeviceType";

/**
 * IngestionHealth
 *
 * Props:
 *   ingestion: {
 *     usage: {
 *       recordsOk: number,
 *       recordsFailed: number,
 *       batchesOk: number,
 *       batchesFailed: number,
 *       lastBatchSize: number,
 *       lastBatchAt: string,              // "dd/MM/yyyy HH:mm:ss" (Athens)
 *       lastBatchSecondsSince: number,    // -1 if never
 *       pausedBatches: number
 *     },
 *     events: { ...same shape... }
 *   }
 *
 * UX:
 * - Top-level status pills show quick health of Usage + Events.
 * - Each panel shows last batch age (progress over 5 minutes), totals, failures, and pause count.
 * - Colors: success (good), warning (stale/backlog risk), danger (failures), secondary (never/paused).
 */
const IngestionHealth = ({ ingestion }) => {
  const { usage = {}, events = {} } = ingestion || {};
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";

  const computeState = (m) => {
    if (!m) return { label: "UNKNOWN", variant: "secondary" };
    if (m.batchesFailed > 0 || m.recordsFailed > 0)
      return { label: "FAILING", variant: "danger" };
    if (m.pausedBatches > 0) return { label: "PAUSED", variant: "secondary" };
    if (m.lastBatchSecondsSince === -1)
      return { label: "NEVER", variant: "secondary" };
    if (m.lastBatchSecondsSince <= 60)
      return { label: "ACTIVE", variant: "success" };
    if (m.lastBatchSecondsSince <= 180)
      return { label: "IDLE", variant: "warning" };
    return { label: "STALE", variant: "warning" };
  };

  const Section = ({ title, data }) => {
    const state = computeState(data);
    const seconds = data.lastBatchSecondsSince;
    const windowSec = 300; // 5-minute visual window
    const progressNow =
      seconds == null || seconds < 0
        ? 0
        : Math.min(100, Math.round((seconds / windowSec) * 100));
    const progressVariant =
      seconds == null || seconds < 0
        ? "secondary"
        : seconds <= 60
        ? "success"
        : seconds <= 180
        ? "warning"
        : "danger";

    const Stat = ({ label, value, muted }) => (
      <div className="d-flex flex-column">
        <div className="text-muted small">{label}</div>
        <div className={`fw-semibold ${muted ? "text-muted" : ""}`}>
          {value}
        </div>
      </div>
    );

    return (
      <div className="border rounded p-3 h-100">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">{title}</div>
          <Badge bg={state.variant} className="px-3 py-2 rounded-pill">
            {state.label}
          </Badge>
        </div>

        <div className="d-flex flex-wrap gap-4 mb-2">
          <Stat
            label="Last batch at (Athens)"
            value={data.lastBatchAt ?? "—"}
          />
          <Stat
            label="Seconds since last"
            value={seconds == null || seconds < 0 ? "never" : `${seconds}s`}
            muted={seconds == null || seconds < 0}
          />
          <Stat label="Batch size" value={data.lastBatchSize ?? "—"} />
        </div>

        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              Silence window over 5 minutes. Lower is better; near 100%
              indicates no recent data.
            </Tooltip>
          }
        >
          <div>
            <ProgressBar now={progressNow} variant={progressVariant} />
          </div>
        </OverlayTrigger>

        <div className="d-flex flex-wrap gap-4 mt-3">
          <Stat label="Batches OK" value={data.batchesOk ?? 0} />
          <Stat label="Batches failed" value={data.batchesFailed ?? 0} />
          <Stat label="Records OK" value={data.recordsOk ?? 0} />
          <Stat label="Records failed" value={data.recordsFailed ?? 0} />
          <Stat label="Paused batches" value={data.pausedBatches ?? 0} />
        </div>
      </div>
    );
  };

  const Pill = ({ title, data }) => {
    const { label, variant } = computeState(data);
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {title} — {label.toLowerCase()}
          </Tooltip>
        }
      >
        <Badge bg={variant} className="rounded-pill px-3 py-2">
          {title}: {label}
        </Badge>
      </OverlayTrigger>
    );
  };

  return (
    <Card className="flex-fill w-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">1NCE Ingestion</Card.Title>
        <div className="d-flex gap-2">
          <Pill title="Usage" data={usage} />
          <Pill title="Events" data={events} />
        </div>
      </Card.Header>

      <Card.Body>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <Section title="Usage Stream" data={usage} />
          </div>
          <div className="col-12 col-lg-6">
            <Section title="Events Stream" data={events} />
          </div>
        </div>

        {/* Legend */}
        {isDesktop && (
          <div className="mt-3 text-muted small">
            <span className="me-3">
              <Badge bg="success">Active</Badge> recent data flowing
            </span>
            <span className="me-3">
              <Badge bg="warning">Idle/Stale</Badge> no recent data or slow flow
            </span>
            <span className="me-3">
              <Badge bg="danger">Failing</Badge> errors seen in last
              batches/records
            </span>
            <span className="me-3">
              <Badge bg="secondary">Paused/Never</Badge> not accepting or no
              history
            </span>
          </div>
        )}
        {(isTablet || isMobile) && (
          <div className="mt-3 text-muted small">
            <div className="mb-3">
              <Badge bg="success">Active</Badge> recent data flowing
            </div>
            <div className="mb-3">
              <Badge bg="warning">Idle/Stale</Badge> no recent data or slow flow
            </div>
            <div className="mb-3">
              <Badge bg="danger">Failing</Badge> errors seen in last
              batches/records
            </div>
            <div className="mb-3">
              <Badge bg="secondary">Paused/Never</Badge> not accepting or no
              history
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default IngestionHealth;
